import { useEffect, useState } from "react";

/**
 * "Install App" prompt button — appears only when the browser fires
 * beforeinstallprompt (i.e. the site is installable and not yet installed).
 * On iOS Safari, shows a hint since it doesn't support the standard prompt.
 */
export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem("pwa_install_dismissed") === "1"; }
    catch { return false; }
  });
  const [platform, setPlatform] = useState("desktop"); // desktop | ios | android

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    const ua = window.navigator.userAgent;
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isAndroid = /android/i.test(ua);
    setPlatform(isIOS ? "ios" : isAndroid ? "android" : "desktop");
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const isStandalone = typeof window !== "undefined" && (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
  if (hidden || isStandalone) return null;

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setHidden(true);
      return;
    }
    // Fallback: platform-specific instructions.
    if (platform === "ios") {
      alert("iPhone / iPad:\n1. Tap the Share button (▲) below\n2. Scroll and tap “Add to Home Screen”\n3. Tap “Add”");
    } else if (platform === "android") {
      alert("Android Chrome:\n1. Tap the ⋮ menu (top-right)\n2. Tap “Install app” or “Add to Home screen”");
    } else {
      alert("Desktop Chrome / Edge:\n1. Click the install icon (▽ / ⤓) in the address bar\n2. Or open the browser menu → “Install Ponderr…”\n\nIf you don't see the option, refresh the page and interact for a few seconds first.");
    }
  };
  const dismiss = (e) => {
    e.stopPropagation();
    setHidden(true);
    try { sessionStorage.setItem("pwa_install_dismissed", "1"); } catch { /* ignore */ }
  };
  return (
    <div className="inline-flex items-center gap-0 border-2 border-black rounded-lg shadow-[2px_2px_0_0_#000] bg-[#FDE047]"
         data-testid="install-pwa-wrap">
      <button onClick={install}
              className="px-3 py-1.5 text-xs sm:text-sm font-bold flex items-center gap-1 hover:bg-[#FCD34D]"
              data-testid="install-pwa-btn"
              title={deferred ? "Install Ponderr as a native-feeling app" : "See install instructions for your device"}>
        📲 <span className="hidden sm:inline">Install Ponderr App</span><span className="sm:hidden">Install</span>
      </button>
      <button onClick={dismiss}
              className="px-2 py-1.5 text-xs border-l-2 border-black hover:bg-[#FCA5A5]"
              data-testid="install-pwa-dismiss"
              title="Dismiss for this session">✕</button>
    </div>
  );
}
